<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */

export interface SectionTab {
    id: string
    label: string
}

const props = defineProps<{
    tabs: SectionTab[]
    activeTabId: string
}>();

const emit = defineEmits<{
    'tab-change': [tabId: string]
}>();

const isActive = (tab: SectionTab) => props.activeTabId === tab.id;

const navigate = (tab: SectionTab) => {
    if (tab.id === props.activeTabId) {
        return;
    }

    emit('tab-change', tab.id);
};
</script>

<template>
    <div class="section-tab-view">
        <nav class="section-tab-view__nav" role="tablist" aria-label="Section tabs">
            <button
                v-for="tab in tabs"
                :key="tab.id"
                class="section-tab-view__tab"
                :class="{ 'section-tab-view__tab--active': isActive(tab) }"
                role="tab"
                type="button"
                :aria-selected="isActive(tab)"
                @click="navigate(tab)"
            >
                {{ tab.label }}
            </button>
        </nav>

        <div class="section-tab-view__actions">
            <slot name="actions" />
        </div>

        <div class="section-tab-view__body">
            <slot />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.section-tab-view {
    border: 1px solid $border-gray-color;
    border-radius: 20px;
    overflow: hidden;

    &__nav {
        display: flex;
        gap: 35px;
        background: transparent;
        padding: 25px 0px 13px 30px;
    }

    &__tab {
        position: relative;
        padding: 0 0 10px;
        border: none;
        background: none;
        cursor: pointer;
        font-family: $font-family-archivo;
        font-size: 20px;
        line-height: 22px;
        font-weight: 400;
        color: $black-color;
        opacity: 0.5;
        white-space: nowrap;
        transition: opacity 0.15s, color 0.15s;

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: -6px;
            right: -6px;
            height: 2px;
            background: $color-secondary;
            border-radius: 1px;
            opacity: 0;
            transition: opacity 0.15s;
        }

        &:hover {
            opacity: 0.75;
        }

        &:focus {
            outline: none;
        }

        &:focus-visible::before {
            content: '';
            position: absolute;
            top: -4px;
            left: -6px;
            right: -6px;
            bottom: 6px;
            border: 2px solid $black-color;
            border-radius: 6px;
            pointer-events: none;
        }

        &--active {
            color: $color-secondary;
            opacity: 1;

            &::after {
                opacity: 1;
            }
        }
    }

    &__actions {
        padding: 16px 24px;
      //  border-bottom: 1px solid $border-gray-color;

        &:empty {
            display: none;
        }
    }

    &__body {
        background: $white-color;
        border: 1px solid $border-gray-color;
        border-radius: inherit;
        // border collapse
        margin: -1px;
    }
}
</style>
